import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router';
import { z } from 'zod';
import { loginRequestSchema } from '@bekebi/contracts/common';
import { ApiError } from '../api/client';
import { Button } from '../components/button';
import { BekebiMark } from '../components/brand';
import { useLogin, type SignedOutReason } from '../session/auth-actions';

/**
 * Sign-in (POST /api/auth/login). The form holds exactly the fields the API accepts: email and
 * password. The password lives only in the form until submission and is cleared afterwards; it is
 * never stored, logged or put in a URL. Error messages are the API's fixed messages or fixed text
 * here — never the submitted values or internal details.
 */

const loginFormSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .max(254, 'That email address is too long.')
    .email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
});
type LoginForm = z.infer<typeof loginFormSchema>;

const NOTICES: Record<SignedOutReason, string> = {
  idle: 'You were signed out after a period of inactivity.',
  expired: 'Your session has ended. Sign in again to continue.',
  signed_out: 'You have signed out.',
  setup_complete: 'The administrator account was created. Sign in to continue.',
  password_set: 'Your password was saved. Sign in with your email address and new password.',
};

function signInErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'invalid_credentials' || error.code === 'rate_limited') return error.message;
    if (error.code === 'network_error') return error.message;
    if (error.status === 400) return 'Check your email address and password, then try again.';
  }
  return 'Sign-in failed. Try again in a moment.';
}

export function LoginPage({ reason }: { reason?: SignedOutReason }) {
  const login = useLogin();
  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(loginFormSchema) });

  const onSubmit = handleSubmit((values) => {
    // The request body is built from the API's own contract, so only accepted fields are sent.
    const body = loginRequestSchema.parse(values);
    login.mutate(body, { onSettled: () => resetField('password') });
  });

  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-canvas">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(ellipse_55%_50%_at_50%_0%,rgb(206_77_31/0.2),rgb(230_158_50/0.06)_55%,transparent)]"
      />
      <div className="relative flex flex-1 items-start justify-center px-4 pb-16 pt-10 sm:items-center sm:pt-0">
        <div className="w-full max-w-[26rem] rounded-2xl border border-line bg-surface px-6 py-8 shadow-2xl shadow-black/40 sm:px-9 sm:py-10">
          <div className="flex justify-center">
            <BekebiMark size={64} />
          </div>
          <h1 className="mt-4 text-center">Welcome to Bekebi Portal</h1>
          <p className="mt-2 text-center text-sm text-muted">Sign in to continue.</p>

          {reason && !login.isError ? (
            <p
              role="status"
              className="mt-6 rounded-lg border border-line bg-raised px-4 py-3 text-sm text-fg-2"
            >
              {NOTICES[reason]}
            </p>
          ) : null}

          {login.isError ? (
            <p
              role="alert"
              className="mt-6 rounded-lg border border-danger-line bg-danger-soft px-4 py-3 text-sm text-fg"
            >
              {signInErrorMessage(login.error)}
            </p>
          ) : null}

          <form onSubmit={onSubmit} noValidate className="mt-7 space-y-5" aria-label="Sign in">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-fg">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className="mt-1.5 block h-11 w-full border px-3.5 text-sm"
                {...register('email')}
              />
              {errors.email ? (
                <p id="email-error" className="mt-1.5 text-sm text-danger">
                  {errors.email.message}
                </p>
              ) : null}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-fg">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className="mt-1.5 block h-11 w-full border px-3.5 text-sm"
                {...register('password')}
              />
              {errors.password ? (
                <p id="password-error" className="mt-1.5 text-sm text-danger">
                  {errors.password.message}
                </p>
              ) : null}
              <p className="mt-2 text-right text-sm">
                <Link
                  to="/forgot-password"
                  className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
                >
                  Forgot password?
                </Link>
              </p>
            </div>

            <Button type="submit" className="h-11 w-full" disabled={login.isPending}>
              {login.isPending ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <p className="mt-7 border-t border-line pt-5 text-center text-xs text-muted">
            Accounts are created by the Bekebi Portal Admin. New users receive an invitation by
            email.
          </p>
        </div>
      </div>
    </main>
  );
}
